import './MembershipCreatorPage.css';
import '../components/creator/creatorSteps.css';

import { useCallback, useMemo, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import StepNavRail, { type CreatorStep } from '../components/creator/StepNavRail';
import StepBasics from '../components/creator/StepBasics';
import StepPricing from '../components/creator/StepPricing';
import StepBenefit from '../components/creator/StepBenefit';
import StepPerks from '../components/creator/StepPerks';
import LiveCustomerCardPreview from '../components/creator/LiveCustomerCardPreview';
import ReadyToPublishChecklist from '../components/creator/ReadyToPublishChecklist';
import {
  isBasicsComplete,
  isBenefitComplete,
  isPerksComplete,
  isPricingComplete,
  isReadyToPublish,
  toUpsertRequest,
  type CreatorDraftPayload,
} from '../components/creator/draftSchema';
import { useMembershipDraft } from '../hooks/useMembershipDraft';
import { useCreateMembershipType } from '../queries';

type Props = {
  routePath?: string;
  onNavigate?: (path: string) => void;
  storeName?: string;
};

const STEP_VALUES: ReadonlyArray<CreatorStep> = [1, 2, 3, 4];

const parseStep = (raw: string | null): CreatorStep => {
  const parsed = Number(raw || '1');
  return (STEP_VALUES as readonly number[]).includes(parsed) ? (parsed as CreatorStep) : 1;
};

const formatLastSaved = (ms: number | null): string => {
  if (!ms) return '';
  const delta = Math.max(0, Date.now() - ms);
  if (delta < 60_000) return 'Draft saved';
  if (delta < 60 * 60_000) return `Draft saved ${Math.floor(delta / 60_000)}m ago`;
  return `Draft saved ${Math.floor(delta / (60 * 60_000))}h ago`;
};

/**
 * Stage 1 creator wizard shell (E1 scaffold).
 *
 * Three-column layout: step rail (left) + active-step content (center) +
 * live-preview rail (right). Step content is a placeholder per step in E1;
 * real form content lands in E2 (Basics + Pricing) and E3 (Perks + Points
 * + Ready-to-publish checklist).
 */
const MembershipCreatorPage = ({ routePath, onNavigate, storeName }: Props) => {
  const draft = useMembershipDraft({ enabled: true });

  const activeStep = useMemo<CreatorStep>(() => {
    const search = String(routePath || '').split('?')[1] || '';
    const rawStep = new URLSearchParams(search).get('step');
    if (rawStep != null) {
      return parseStep(rawStep);
    }
    const resumedStep = Math.max(1, Math.min(draft.stepReached || 1, 4));
    return resumedStep as CreatorStep;
  }, [draft.stepReached, routePath]);

  const handleStepChange = useCallback(
    (next: CreatorStep) => {
      if (next === activeStep) return;
      const base = (routePath || '/store/memberships/new').split('?')[0];
      const params = new URLSearchParams(routePath?.split('?')[1] || '');
      params.set('step', String(next));
      onNavigate?.(`${base}?${params.toString()}`);
    },
    [activeStep, onNavigate, routePath]
  );

  const handleBack = useCallback(() => onNavigate?.('/store/memberships'), [onNavigate]);

  const payload = draft.payload as CreatorDraftPayload;
  const basicsValid = isBasicsComplete(payload);
  const pricingValid = isPricingComplete(payload);
  const benefitValid = isBenefitComplete(payload);
  const perksValid = isPerksComplete(payload);
  const readyToPublish = isReadyToPublish(payload);
  void perksValid;

  const createMutation = useCreateMembershipType();
  const [publishError, setPublishError] = useState<string | null>(null);

  const handleFieldChange = useCallback(
    (partial: Partial<CreatorDraftPayload>) => {
      draft.patch(partial as Record<string, unknown>);
    },
    [draft]
  );

  const canContinueFromActive =
    activeStep === 1 ? basicsValid
    : activeStep === 2 ? pricingValid
    : activeStep === 3 ? benefitValid
    : true;

  const handlePublish = useCallback(async () => {
    if (!readyToPublish) return;
    setPublishError(null);
    try {
      await draft.flush();
      await createMutation.mutateAsync(toUpsertRequest(payload));
      await draft.discard();
      onNavigate?.('/store/memberships');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Could not publish membership.';
      setPublishError(message);
    }
  }, [createMutation, draft, onNavigate, payload, readyToPublish]);

  const handleContinue = useCallback(() => {
    if (!canContinueFromActive) return;
    const next = Math.min(4, activeStep + 1) as CreatorStep;
    // Mark progress reached before navigating so the next step is unlocked
    // even before the merchant edits anything.
    draft.patch({}, next);
    handleStepChange(next);
  }, [activeStep, canContinueFromActive, draft, handleStepChange]);

  const handleBackStep = useCallback(() => {
    const prev = Math.max(1, activeStep - 1) as CreatorStep;
    handleStepChange(prev);
  }, [activeStep, handleStepChange]);

  const handleDiscard = useCallback(async () => {
    await draft.discard();
    onNavigate?.('/store/memberships');
  }, [draft, onNavigate]);

  const stepReached = Math.max(activeStep, draft.stepReached) as CreatorStep;

  return (
    <div className="creator-page" data-testid="creator-page">
      <nav className="creator-page__breadcrumb" aria-label="Breadcrumb">
        <button type="button" className="creator-page__breadcrumb-link" onClick={handleBack}>
          Memberships
        </button>
        <ChevronRight size={14} aria-hidden="true" />
        <span className="creator-page__breadcrumb-current">New membership</span>
      </nav>

      <header className="creator-page__header">
        <div>
          <h1 className="creator-page__title">Create membership</h1>
          <p className="creator-page__subtitle">
            Set up a loyalty tier customers can join. Draft saves automatically as you go.
          </p>
        </div>
        <div className="creator-page__saved" data-testid="creator-saved-indicator">
          {draft.isSaving ? 'Saving…' : formatLastSaved(draft.lastSavedAt)}
        </div>
      </header>

      <div className="creator-page__grid">
        <aside className="creator-page__rail">
          <StepNavRail
            activeStep={activeStep}
            stepReached={stepReached}
            onChange={handleStepChange}
          />
        </aside>

        <section className="creator-page__main" data-testid={`creator-step-content-${activeStep}`}>
          {activeStep === 1 ? (
            <StepBasics payload={payload} onChange={handleFieldChange} />
          ) : activeStep === 2 ? (
            <StepPricing payload={payload} onChange={handleFieldChange} />
          ) : activeStep === 3 ? (
            <StepBenefit payload={payload} onChange={handleFieldChange} />
          ) : (
            <StepPerks payload={payload} onChange={handleFieldChange} />
          )}

          {publishError ? (
            <div className="creator-page__publish-error" data-testid="creator-publish-error">
              {publishError}
            </div>
          ) : null}

          <footer className="creator-page__footer">
            <button
              type="button"
              className="creator-page__discard"
              onClick={handleDiscard}
              data-testid="creator-discard"
            >
              Discard draft
            </button>
            <div className="creator-page__nav-actions">
              {activeStep > 1 ? (
                <button
                  type="button"
                  className="creator-page__back"
                  onClick={handleBackStep}
                  data-testid="creator-back"
                >
                  Back
                </button>
              ) : null}
              {activeStep < 4 ? (
                <button
                  type="button"
                  className="creator-page__continue"
                  onClick={handleContinue}
                  disabled={!canContinueFromActive}
                  data-testid="creator-continue"
                >
                  Continue to {activeStep === 1 ? 'pricing' : activeStep === 2 ? 'benefit' : 'perks'}
                </button>
              ) : (
                <button
                  type="button"
                  className="creator-page__continue"
                  onClick={handlePublish}
                  disabled={!readyToPublish || createMutation.isPending}
                  data-testid="creator-publish"
                >
                  {createMutation.isPending ? 'Publishing…' : 'Publish membership'}
                </button>
              )}
            </div>
          </footer>
        </section>

        <aside className="creator-page__preview" data-testid="creator-preview-rail">
          <div className="creator-page__preview-eyebrow">LIVE PREVIEW · CUSTOMER CARD</div>
          <LiveCustomerCardPreview payload={payload} storeName={storeName} />
          <div className="creator-page__preview-eyebrow">READY TO PUBLISH</div>
          <ReadyToPublishChecklist payload={payload} />
        </aside>
      </div>
    </div>
  );
};

export default MembershipCreatorPage;
